/* eslint-disable prettier/prettier */
<template>
  <page-loader>
    <v-container>
      <v-row no-gutters>
        <v-col class="d-flex align-center justify-space-between">
          <div class="d-flex">
            <select-list
              key="Site"
              v-model="query.params['site']"
              :items="mappedSites"
              label="site"
              :disabled="hasChanges"
              placeholder="Select Site"
              class="mr-4"
              hide-details
              @change="resetOffers"
            />
            <select-list
              key="Language"
              v-model="query.params['language']"
              custom-type="language"
              :items="getItemsForOption('translationLanguages')"
              label="Language"
              :disabled="hasChanges"
              placeholder="Select language"
              class="mr-4"
              hide-details
              @change="resetOffers"
            />
          </div>
          <v-tooltip max-width="300" bottom>
            <template #activator="{ on }">
              <base-chip v-show="hasChanges" color="blue" text="Changes Detected" icon="info" dark small v-on="on">
              </base-chip>
            </template>
            <span>Please save to see changes</span>
          </v-tooltip>
          <v-tooltip max-width="300" bottom>
            <template #activator="{ on }">
              <base-chip
                v-show="!checkOrigin && !hasChanges"
                color="warning"
                text="Missing Navigation"
                icon="info"
                dark
                small
                v-on="on"
              >
              </base-chip>
            </template>
            <span>Please make changes and save to create this navigation</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>
    <v-container v-if="navigations">
      <draggable v-if="query.params['site']" v-model="navigations" @change="checkMove">
        <Menuitem
          v-for="(item, index) in navigations"
          :key="index"
          :nav-index="index"
          :item="item"
          @updateNav="onUpdateNav($event, index)"
          @deleteNav="onDeleteNav($event, index)"
          @changeNav="onUpdateNav($event, index)"
          @orderNav="checkMove"
        />
      </draggable>
    </v-container>
    <v-container v-else>
      <v-card v-if="query.params['site']" class="ml-2 d-flex px-2 12 py-2 align-center justify-end"></v-card>
    </v-container>
    <v-container v-if="query.params['site']">
      <v-card flat class="ml-2 d-flex px-2 12 py-2 align-center justify-end">
        <row-create type="menu" create-text="Add new menu item" @click="addTopLevel" />
      </v-card>
    </v-container>
    <generic-modal
      v-model="showPageCreateModal"
      title="Create New Page"
      hide-louis
      @modal-confirm="onUpdateNav($event, index, newTopLevel)"
    >
      <text-input v-model="newTopLevel.pageTitle" label="Page Title" />
      <text-input v-model="newTopLevel.pageUrl" label="Page URL" />
      <toggle-input
        v-model="newTopLevel.includeInSitemap"
        :disabled="isLoading"
        class="mb-6"
        label="Include in Sitemap"
      />
      <toggle-input
        v-model="newTopLevel.includeInNavigation"
        :disabled="isLoading"
        class="mb-6"
        label="Include in Navigation"
      />
      <select-list
        v-model="newTopLevel.pageConfig.landingPageTemplateSlug"
        :items="mappedPageTemplates"
        label="Landing Page"
        hide-details="auto"
      />
      <select-list
        v-model="newTopLevel.pageConfig.landingPageFunnelSlug"
        :items="mappedPageTemplates"
        class="mt-4"
        label="Funnel Page"
        hide-details="auto"
      />
      <select-list
        v-model="newTopLevel.pageConfig.offerSlug"
        :items="mappedOffers"
        class="mt-4"
        label="Offer"
        hide-details
      />
      <select-list
        v-model="newTopLevel.pageConfig.coin"
        :items="coinOffers"
        class="mt-4"
        clearable
        label="Coin"
        hide-details
      />
      <select-list
        v-model="newTopLevel.pageConfig.stock"
        :items="stockOffers"
        class="mt-4"
        clearable
        label="Stock"
        hide-details
      />
    </generic-modal>
    <fab-actions
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      context="Page"
      @save="showSaveModal = true"
      @reset="showResetModal = true"
    />
    <reset-modal
      v-model="showResetModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      type="order"
      @modal-confirm="onResetConfirm"
    />
    <save-modal v-model="showSaveModal" title="Save Changes" @modal-confirm="onSaveConfirm">
      <template #subtitle>
        <div>Please confirm that the changes you've made below are satisfactory</div>
      </template>
      <h3 class="text-h5">Page Information</h3>
    </save-modal>
  </page-loader>
</template>

<script>
import { merge } from 'lodash-es'
import { mapGetters } from 'vuex'
import { cloneDeep } from 'lodash-es'

import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'

import { navigations, landingPages, offers, sites } from '@/services/offers'

import TextInput from '@/components/forms/text-input'
import Draggable from 'vuedraggable'
import Menuitem from './components/menu-item'
import RowCreate from '@/components/row-create'
import GenericModal from '@/components/modals/generic-modal'
import FabActions from '@/components/fab-actions'
import ResetModal from '@/components/modals/reset-modal'
import SaveModal from '@/components/modals/save-modal'
import BaseChip from '@/components/chips/base-chip'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import ToggleInput from '@/components/forms/toggle-input'
import SelectList from '@/components/forms/select-list'

const blankNewPage = () => ({
  site: '',
  name: '',
  navigation: {
    default: {
      navigationItems: {},
    },
  },
})

const blankNewTopLevel = () => ({
  pageTitle: '',
  pageUrl: '',
  pageConfig: {
    landingPageFunnelSlug: '',
    landingPageTemplateSlug: '',
    language: '',
    offerSlug: '',
    coin: '',
    stock: '',
  },
  dynamicSubNavigationConfig: {
    pageUrl: '',
    landingPageFunnelSlug: '',
    landingPageTemplateSlug: '',
    coin: '',
    stock: '',
    limit: 0,
    offerType: '',
    offers: [],
  },
  includeInSitemap: false,
  includeInNavigation: false,
  isDynamicSubNavigation: false,
  subNavigationItems: [],
})

export default {
  name: 'NavigationOrdering',
  components: {
    Draggable,
    Menuitem,
    TextInput,
    SaveModal,
    FabActions,
    GenericModal,
    RowCreate,
    ResetModal,
    BaseChip,
    PageLoader,
    ToggleInput,
    SelectList,
  },
  mixins: [DataModification, PermissionsHandler, QueryParamsHandler],
  props: {
    department: {
      type: String,
      required: true,
      validator(value) {
        return ['performance'].includes(value)
      },
    },
  },
  data: () => ({
    showPageCreateModal: false,
    showSaveModal: false,
    createNavigation: false,
    navigations: [],
    navigationData: [],
    isLoading: false,
    showResetModal: false,
    navigationOriginal: {},
    navigationRealOriginal: {},
    isDynamicSubNavigation: false,
    navigationUpdated: {},
    queriesDefined: ['language', 'site'],
    queryDefaults: ['language', 'site'],
    defaultQuery: { site: 'ourlovelytestingdomain.com' },
    newPage: blankNewPage(),
    newTopLevel: blankNewTopLevel(),
    sites: [],
    pageTemplates: [],
    offers: [],
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors', 'isPageLoading']),
    ...mapGetters('options', ['options', 'formattedOption', 'getItemsForOption', 'hasOptions']),
    mappedPageTemplates() {
      return this.pageTemplates.map(({ slug, template: { default: { templateName } } }) => ({
        key: templateName || slug,
        value: slug,
      }))
    },
    mappedSites() {
      return this.sites.map(({ name, hostname }) => ({
        key: name || hostname,
        value: hostname,
      }))
    },
    mappedOffers() {
      return this.offers.map(({ slug, type, name }) => ({
        key: `${name || slug} (${this.formattedOption('offerTypes', type)})`,
        value: slug,
        type,
      }))
    },
    hasChanges() {
      return this.hasDataChanged(this.navigationOriginal, this.navigationUpdated)
    },
    checkOrigin() {
      return this.navigationRealOriginal.id !== undefined ? true : false
    },
    coinOffers() {
      return (this.options && this.options.entities && this.options.entities.cryptoCoin) || []
    },
    stockOffers() {
      return (this.options && this.options.entities && this.options.entities.tradingStock) || []
    },
  },
  async created() {
    await Promise.all([this.getLandingPageTemplates(), this.getOffers()])
    await this.getNavigations()
    this.getPerformanceSites()
    this.resetOffers()
  },
  methods: {
    async getLandingPageTemplates() {
      this.pageTemplates = await landingPages.getAllLandingPageTemplates()
    },
    async getOffers() {
      try {
        const { data } = await offers.getAllOffers({ compact: true })
        this.offers = data
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve offers',
        })
      }
    },
    async resetOffers() {
      await this.getNavigations()
    },
    async getNavigations() {
      const { language, site } = this.query.params
      this.isLoading = true
      const { data } = await navigations.getAll({ filters: { site } })
      if (data.length) {
        const navigation = data[0].navigation.default.navigationItems[language]
        if (!data[0].navigation.default.navigationItems[language]) {
          data[0].navigation.default.navigationItems[language] = []

          data[0].navigation.default.navigationItems[language].push(
            {
              pageTitle: 'Home',
              pageUrl: '/',
              pageConfig: {
                landingPageFunnelSlug: '',
                landingPageTemplateSlug: '',
                language: '',
                offerSlug: '',
                coin: '',
                stock: '',
              },
              dynamicSubNavigationConfig: {
                pageUrl: '',
                landingPageFunnelSlug: '',
                landingPageTemplateSlug: '',
                coin: '',
                stock: '',
                limit: 0,
                offerType: '',
                offers: [],
              },
              includeInSitemap: false,
              includeInNavigation: false,
              isDynamicSubNavigation: false,
              subNavigationItems: [],
            },
            {
              pageTitle: '404',
              pageUrl: '/404',
              pageConfig: {
                landingPageFunnelSlug: '',
                landingPageTemplateSlug: '',
                language: '',
                offerSlug: '',
                coin: '',
                stock: '',
              },
              dynamicSubNavigationConfig: {
                pageUrl: '',
                landingPageFunnelSlug: '',
                landingPageTemplateSlug: '',
                coin: '',
                stock: '',
                limit: 0,
                offerType: '',
                offers: [],
              },
              includeInSitemap: false,
              includeInNavigation: false,
              isDynamicSubNavigation: false,
              subNavigationItems: [],
            }
          )
          this.navigations = data[0].navigation.default.navigationItems[language]
        } else {
          this.navigations = navigation
        }
        this.navigationRealOriginal = cloneDeep(data[0])
        this.navigationOriginal = cloneDeep(data[0])
        this.navigationUpdated = cloneDeep(data[0])
      } else {
        this.navigationUpdated = merge({}, this.newPage, {
          site,
          name: site,
          navigation: { default: { navigationItems: { [language]: [] } } },
        })
        this.navigationRealOriginal = merge({}, this.newPage, {
          site,
          name: site,
          navigation: { default: { navigationItems: { [language]: [] } } },
        })

        this.navigationUpdated.navigation.default.navigationItems[language].push(
          {
            pageTitle: 'Home',
            pageUrl: '/',
            pageConfig: {
              landingPageFunnelSlug: '',
              landingPageTemplateSlug: '',
              language: '',
              offerSlug: '',
              coin: '',
              stock: '',
            },
            dynamicSubNavigationConfig: {
              pageUrl: '',
              landingPageFunnelSlug: '',
              landingPageTemplateSlug: '',
              coin: '',
              stock: '',
              limit: 0,
              offerType: '',
              offers: [],
            },
            includeInSitemap: false,
            includeInNavigation: false,
            isDynamicSubNavigation: false,
            subNavigationItems: [],
          },
          {
            pageTitle: '404',
            pageUrl: '/404',
            pageConfig: {
              landingPageFunnelSlug: '',
              landingPageTemplateSlug: '',
              language: '',
              offerSlug: '',
              coin: '',
              stock: '',
            },
            dynamicSubNavigationConfig: {
              pageUrl: '',
              landingPageFunnelSlug: '',
              landingPageTemplateSlug: '',
              coin: '',
              stock: '',
              limit: 0,
              offerType: '',
              offers: [],
            },
            includeInSitemap: false,
            includeInNavigation: false,
            isDynamicSubNavigation: false,
            subNavigationItems: [],
          }
        )

        this.navigationOriginal = this.navigationUpdated

        this.navigations = this.navigationUpdated.navigation.default.navigationItems[language]
      }
      this.setPageLoading(false)
      this.isLoading = false
      // console.log(this.navigationRealOriginal.id)
    },
    async getPerformanceSites() {
      this.isLoading = true

      try {
        const { data } = await sites.getAllSites({ filters: { department: this.department } })
        this.sites = data
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve sites',
        })
      }

      this.isLoading = false
    },
    onUpdateNav(value, index, event) {
      const { language, site } = this.query.params
      // update if adding new
      if (!this.navigationOriginal.id) {
        // if editing
        if (!event) {
          const newNavigation = this.navigations
            .slice(0, index)
            .concat(value)
            .concat(this.navigations.slice(index + 1)) // :D
          this.navigationUpdated.navigation.default.navigationItems[language] = newNavigation
          this.navigations = this.navigationUpdated.navigation.default.navigationItems[language]

          this.newTopLevel = blankNewTopLevel()
          this.showPageCreateModal = false
          // if adding new
        } else {
          this.navigationOriginal = this.navigations
          this.navigationUpdated.navigation.default.navigationItems[language].push(event)
          this.newTopLevel = blankNewTopLevel()
          this.showPageCreateModal = false
        }
      } else {
        // if (this.navigations) {
        const newNavigation = this.navigations
          .slice(0, index)
          .concat(value)
          .concat(this.navigations.slice(index + 1)) // :D
        this.newTopLevel = blankNewTopLevel()
        this.showPageCreateModal = false
        // }
        // if adding, not editing

        if (!value) {
          if (!this.navigationUpdated.navigation.default.navigationItems[language]) {
            this.navigationUpdated.navigation.default.navigationItems[language] = []
          }
          this.navigationUpdated.navigation.default.navigationItems[language].push(event)
        } else {
          this.navigationUpdated.navigation.default.navigationItems[language] = newNavigation
        }
        this.navigations = this.navigationUpdated.navigation.default.navigationItems[language]
      }
    },
    checkMove: function(e) {
      const { language, site } = this.query.params
      this.navigationUpdated.navigation.default.navigationItems[language] = this.navigations
      this.createNavigation = true
    },
    onDeleteNav(value, index) {
      const { language, site } = this.query.params

      if (this.checkOrigin) {
        const newNavigation = this.navigations
          .slice(0, index)
          .concat(value)
          .concat(this.navigations.slice(index + 1)) // :D
        this.navigationUpdated.navigation.default.navigationItems[language] = newNavigation.filter(Boolean)
        this.navigations = this.navigationUpdated.navigation.default.navigationItems[language]
        this.newTopLevel = blankNewTopLevel()
      } else {
        this.navigationOriginal = this.navigationRealOriginal

        const newNavigation = this.navigations
          .slice(0, index)
          .concat(value)
          .concat(this.navigations.slice(index + 1)) // :D
        this.navigationUpdated.navigation.default.navigationItems[language] = newNavigation.filter(Boolean)
        this.navigations = this.navigationUpdated.navigation.default.navigationItems[language]
        this.newTopLevel = blankNewTopLevel()
      }
    },
    addTopLevel(data) {
      this.showPageCreateModal = true
    },
    onResetConfirm() {
      this.resetOffers()
      this.$notify({
        type: 'success',
        text: 'Your changes have been reverted',
      })
      this.showResetModal = false
    },
    async onSaveConfirm() {
      const { language, site } = this.query.params
      if (!this.navigationOriginal.id) {
        this.showSaveModal = false
        await navigations.createNavigation(this.navigationUpdated)
      } else {
        await navigations.updateNavigation(this.navigationOriginal.id, this.navigationUpdated)
        this.showSaveModal = false
      }
      this.resetOffers()
    },
  },
}
</script>
